import { BusinessAutomationActionTypeEnum, BusinessAutomationTriggerTypeEnum, dateBasedTriggerMetadataSchema, eventBasedTriggerMetadataSchema, oneTimeTriggerMetadataSchema, ReputationTemplateTypeEnum, TimezoneEnum, } from '@reach/shared-types/business-automation';
import { dynamicMergeFieldSchema, staticMergeFieldSchema, } from '@reach/shared-types/communication-groups';
import { BusinessSegmentConditionsSchema } from '@reach/shared-types/conditions';
import { ReachFeature } from '@reach/shared-types/features';
import { z } from 'zod';
export { ReputationTemplateTypeEnum } from './business-automation.js';
export var AutomationTemplateSegmentTypeEnum;
(function (AutomationTemplateSegmentTypeEnum) {
    AutomationTemplateSegmentTypeEnum["ALL_USERS"] = "all_users";
})(AutomationTemplateSegmentTypeEnum || (AutomationTemplateSegmentTypeEnum = {}));
export var AutomationTemplateVisibilityEnum;
(function (AutomationTemplateVisibilityEnum) {
    AutomationTemplateVisibilityEnum["VISIBLE"] = "visible";
    AutomationTemplateVisibilityEnum["HIDDEN"] = "hidden";
})(AutomationTemplateVisibilityEnum || (AutomationTemplateVisibilityEnum = {}));
/**
 * Reputation-specific sub-types. Discriminates the role a reputation
 * automation plays in the tenant's selected review flow.
 */
export const ReputationFeatureSubTypeEnum = {
    INTERNAL_REVIEW: 'internal-review',
    GOOGLE_REVIEW: 'google-review',
    POST_INTERNAL_REVIEW: 'post-internal-review',
    BOTH_REVIEWS: 'both-reviews',
};
export const reputationFeatureSubTypeSchema = z.enum(Object.values(ReputationFeatureSubTypeEnum));
/**
 * Zod schema for the automation template feature_sub_type column.
 */
export const automationFeatureSubTypeSchema = reputationFeatureSubTypeSchema.nullable();
/**
 * Response schema for a single automation template
 * Note: We use z.any() for contents to avoid circular dependencies with database package
 * The full type validation happens on the backend
 */
export const automationTemplateResponseSchema = z.object({
    id: z.string().describe('Template ID'),
    name: z.string().describe('Template name'),
    version: z.string().describe('Template version (semver)'),
    visibility: z.enum(['visible', 'hidden']).describe('Template visibility'),
    featureSubType: automationFeatureSubTypeSchema.describe('Sub-type that qualifies the template within its feature, or null when not applicable'),
    contents: z.any().describe('Template contents (full schema validated on backend)'),
});
// Convenience export for API usage
export const automationTemplateResponse = automationTemplateResponseSchema;
const extraMergeFieldsSchemaForTemplates = z.array(z.union([staticMergeFieldSchema, dynamicMergeFieldSchema]));
const segmentDefault = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    conditions: BusinessSegmentConditionsSchema,
});
const segmentOptions = z.array(z.union([
    segmentDefault,
    z.string().uuid(), // Partner segment definition ID (UUID)
]));
const baseTemplateActionSchema = z.object({
    /**
     * Stable ID for this template action, used to preserve automation action IDs during sync
     */
    templateActionId: z.string().uuid().optional(),
    index: z.number(),
});
export const automationTemplateContents = z.object({
    name: z.string(),
    description: z.string(),
    feature: z.nativeEnum(ReachFeature).optional(),
    triggerType: z.nativeEnum(BusinessAutomationTriggerTypeEnum),
    /**
     * @see business-automation.types.ts
     */
    triggerMetadata: z.discriminatedUnion('triggerType', [
        oneTimeTriggerMetadataSchema.extend({
            shouldOverrideActiveAutomations: z
                .boolean()
                .optional()
                .describe('If true, even automations that are marked active will sync the trigger metadata (e.g. even if a user turned it on and it says Jan 1, if you update + sync to Jan 2, this will override the active automation and set the date to Jan 2)'),
        }),
        /**
         * We dont need the rootActionId for the template, that'll be
         * filled in when we build the automation
         */
        eventBasedTriggerMetadataSchema.omit({ rootActionId: true }).extend({
            shouldOverrideActiveAutomations: z
                .boolean()
                .optional()
                .describe('If true, even automations that are marked active will sync the trigger metadata (e.g. even if a user turned it on and it says Jan 1, if you update + sync to Jan 2, this will override the active automation and set the date to Jan 2)'),
        }),
        dateBasedTriggerMetadataSchema.omit({ rootActionId: true }).extend({
            shouldOverrideActiveAutomations: z
                .boolean()
                .optional()
                .describe('If true, even automations that are marked active will sync the date (e.g. even if a user turned it on and it says Jan 1, if you update + sync to Jan 2, this will override the active automation and set the date to Jan 2)'),
        }),
    ]),
    actionData: z.array(z.discriminatedUnion('type', [
        baseTemplateActionSchema.extend({
            type: z.literal(BusinessAutomationActionTypeEnum.SEND_COMMUNICATION),
            html: z.string(),
            css: z.string(),
            /**
             * Compiled html to use if the user has not updated the html above
             */
            compiledHtml: z.string(),
            text: z.string(),
            subject: z.string(),
            previewText: z.string(),
            extraMergeFields: extraMergeFieldsSchemaForTemplates.optional(),
            /**
             * Optional form template keys to reference
             * These reference form templates by their template_key
             * Forms will be synced from form templates before automation sync
             */
            formTemplateKeys: z.array(z.string()).optional(),
            customBlockText: z.string().optional(),
            /**
             * Specifies the type of reputation communication (e.g., feedback-request, google-review)
             */
            reputationTemplateType: z.nativeEnum(ReputationTemplateTypeEnum).optional(),
        }),
        baseTemplateActionSchema.extend({
            type: z.literal(BusinessAutomationActionTypeEnum.WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME),
            hour: z.number(),
            minute: z.number(),
            timezone: z.nativeEnum(TimezoneEnum),
        }),
        baseTemplateActionSchema.extend({
            type: z.literal(BusinessAutomationActionTypeEnum.WAIT_UNTIL_NEXT_OCCURRENCE),
            hour: z.number(),
            minute: z.number(),
            timezone: z.nativeEnum(TimezoneEnum),
        }),
        baseTemplateActionSchema.extend({
            type: z.literal(BusinessAutomationActionTypeEnum.WAIT_FOR_DURATION),
            days: z.number().min(0).default(0),
            hours: z.number().min(0).default(0),
            minutes: z.number().min(0).default(0),
            seconds: z.number().min(0).default(0),
        }),
        baseTemplateActionSchema.extend({
            type: z.literal(BusinessAutomationActionTypeEnum.CHECK_SEGMENT_MEMBERSHIP),
            segmentIds: z.array(z.string()),
        }),
    ])),
    includeSegmentDefaults: segmentOptions,
    excludeSegmentDefaults: segmentOptions.optional(),
    /**
     * The priority of the automation template, this is used to order managed automations
     * when they are synced to businesses
     */
    priority: z.number().optional(),
    /**
     * Template default for whether tenant automations should use the point-of-contact sender when the partner allows it.
     * Omitted/undefined is stored as unset; only explicit true should mean opt-in once send-time behavior exists.
     */
    usePointOfContactSender: z.boolean().optional(),
    /**
     * The `profileId` of the selected sender profile from
     * partner_schema_mappings.contactsSchema.primarySource.senderProfiles.
     * null = no profile selected; undefined = field unset (legacy row).
     */
    senderProfileId: z.string().uuid().nullable().optional(),
});
export const defaultAutomationTemplateMetadataSchema = z.object({
    name: z.string().describe('Template name identifier'),
    version: z.string().describe('Template version (semver)'),
    displayName: z.string().describe('Human-readable template name'),
    description: z.string().describe('Template description'),
});
//# sourceMappingURL=automation-template.js.map